const article = {
	slug: "orange-ribbon-hunt",
	title: "A Halloween door hunt that never shows the apartment number",
	date: "4 November 2026",
	updated: null,
	description:
		"A neighbor said our building had no list of the doors giving out Halloween candy. Two hours later it had an app that hides every apartment number. Here are the three workflows, the privacy decisions, the final numbers and the abuse problems still open.",
	keywords: [
		"claude code",
		"privacy by design",
		"halloween",
		"community app",
		"cloudflare d1",
		"abuse prevention",
		"progressive web app",
	],
	tldr:
		"A list of who is home, with kids knocking, is not a harmless list. So residents appear under silly names, the apartment number stays with the resident and the organizer, and the app never asks who you are. 17 phones showed up and nobody finished a round. Next comes an open source version any building can run, with three abuse problems still open.",
	readingTime: null,
	provenance: "Written from my build notes, November 2026.",
	provenanceUrl: null,
	series: null,
	artifact: null,
	hero: {
		src: "/articles-media/orange-ribbon-hunt-hero.png",
		alt: "The Orange Ribbon Hunt title card: an orange bow beside the line, Find every ribbon door. Collect the secret words. Rule the towers.",
		caption: "The app's share card.",
	},
	markdown: `At 3:07 PM on Halloween, my building had no list of the doors giving out candy. At 5:13 PM, it had a working app.

In our building, a door with an orange ribbon means candy. A neighbor put the problem plainly in the group chat: you have to go to each floor to find the ribbons, and there is no list of participating units.

I built it from the back seat of a car, on my phone, with Claude Code. Backseat developer, episode two. [Episode one](/article/broken-bottle-remote-3d-print) was a caddy for my son's glass bottles.

## The decision before the code

The obvious build is a shared list of apartment numbers. I did not want that list to exist in a group chat, a spreadsheet or a screenshot.

A list of who is home, with kids knocking, is not a harmless list. It tells anyone who reads it which doors will open to a stranger tonight, and it outlives the night.

So the rules came first:

- **Residents appear under silly names.** A door is "Baron Gummy Bat, tower 1, floor 4", never an apartment number. The orange ribbon in the hallway does the rest.
- **Two people see the apartment number.** The resident who signed up, and the organizer. Nobody else.
- **The app never asks who you are.** Families get a made-up team name. There are no accounts, emails or phone numbers.
- **Usage is counted without identities.** The app counts events like visits and stamps. Rate limits use a salted hash, so no IP address is stored.
- **It ends on its own.** The game closed itself at noon the next day. The organizer then saves the door list for next year and deletes everything.

## Workflow 1: residents giving out candy

![Resident workflow from a test copy with invented data. 1: Sign up with tower, floor and apartment. 2: Get a secret name and a secret word. 3: Star the costumes that knock.](/articles-media/orange-ribbon-hunt-resident.jpg)

1. **Sign up the door.** Enter tower, floor and apartment. Add an optional hint like "right off the elevator", and tick the box if you have a nut-free option.
2. **Get a secret name and a secret word.** Hang the ribbon. When kids knock, give them candy and the word.
3. **Watch the visits and star the costumes.** The screen counts visits and lists the teams that came by, so a resident can star a costume they loved. Stars are the only costume score kids ever get.
4. **Run out gracefully.** "We are out of candy" keeps the door on the list without sending more kids. "Remove my door" takes it off entirely.

"Open this door on another phone" and "Take the kids hunting too" exist because one parent hands out candy while the other walks the floors.

## Workflow 2: families hunting

![Hunter workflow from a test copy. 1: Enter the building code. 2: Get a team name and a list by floor. 3: Type the word the resident gives you. 4: Collect the door and climb a rank.](/articles-media/orange-ribbon-hunt-hunter.jpg)

1. **Enter the building code.** It was shared in the group chat and at reception. It keeps out casual visitors, not a determined one.
2. **Get a team name and the list.** Doors are grouped by tower and floor, with how many are left on each floor. A nut-free filter shows only doors that offer one.
3. **Type the secret word at the door.** The word proves a visit, and it stamps the door for your team.
4. **Climb the ranks.** Teams go from Fresh Ghost to Ruler of the Towers. Typing TREAT or TRICK, or tapping the title five times, sets off small surprises for the kids.

The parent holds the phone, so every line of text is written for the parent. The footer reminds them to stay with their children, knock only on ribbon doors, and check every label, because nut-free is what the resident told us.

![Extras from the same test copy: add a ribbon door you found, which shows as Found by a neighbor; rate candy and costume after a visit; a leaderboard by made-up team name.](/articles-media/orange-ribbon-hunt-extras.jpg)

Families can also add a ribbon they spot that is not on the list. Later that night, after a neighbor joked in the chat that we needed candy and costume ratings, I added those too. A team can rate only a door it visited. A door appears on a public top list only after at least 3 ratings averaging 3 stars or more. That keeps ratings anonymous, and a low score is never shown.

## Workflow 3: the organizer

![Organizer view from a test copy with invented doors: usage counts with no names or IP addresses, and the door table, the only screen that shows apartment numbers.](/articles-media/orange-ribbon-hunt-organizer.jpg)

The organizer page is the one place apartment numbers appear, and it says so at the top. From there the organizer can:

- See usage counts by hour, with no names or IP addresses.
- Add a door, mark one out of candy, or remove one.
- Load a list of doors, and save the list for next year.
- Change the title, the building code and the closing time.
- Read feedback from the in-app button.
- Delete all data when the night is over.

## The safeguard I removed

The first version hid any door a neighbor reported until a second phone confirmed it. That stops one person from putting a stranger's door on the list.

On one night in one building, that second phone might never come. I removed the check. A reported door shows its door number right away, marked "Found by a neighbor", until the resident signs up and it becomes a silly name.

I shipped with that risk on purpose, for one night. It does not ship that way again.

## How it was built

- **Cloudflare Pages, Functions and D1.** Static pages, a small API and a SQLite database. I picked it over Vercel because Vercel storage needed a terms step in a browser, and I had one hour.
- **A plain JavaScript app that installs to the home screen.** The service worker never caches the API, so a closed game or a removed door shows up right away.
- **Tests that tap like a phone.** Unit tests cover the game rules. A headless Chrome script signs up doors, stamps them and rates them on a local copy, then checks tap sizes and sideways scrolling on a small phone. Every screenshot in this article shows test data, with invented doors and apartment numbers.

| Time on Halloween | What happened |
|---|---|
| 3:07 PM | I read the "no list" message |
| 4:27 PM | First commit |
| 5:13 PM | Link in the group chat |
| 9:20 PM | Candy and costume ratings added |
| Noon, next day | The game closed itself |

## What happened

| Step | Count |
|---|---|
| Phones that opened it, including mine | 17 |
| Doors on the list | 4 |
| Teams | 2 |
| Secret words collected | 0 |

People showed up, and nobody finished a round of the game.

The miss was timing. Halloween afternoon was too late. One neighbor signed up after kids had already come by, then asked to start early next year. Residents also posted door numbers straight into the chat, and at least one never reached the app. Next year's version has to meet people in the chat, days before Halloween, when the ribbons are handed out.

Another neighbor wrote that we should definitely do this next year.

## What comes next

The next step is a generic version any apartment building can run, with its own name, code, towers and closing time. I will open source it, so people who know abuse prevention can add the defenses this version does not have.

Three problems are open, and they are the first things I would want help with:

1. **Proving a door belongs to the person claiming it.** Today anyone with the building code can sign up any door number.
2. **Stopping fake teams from gaming the candy ratings.** Teams are free to create, so the 3-rating minimum slows a determined parent down but does not stop one.
3. **Limiting the damage when the building code leaks.** A code shared in a group chat will not stay inside the building forever.

Until those are solved, every building that runs it carries the same risks this one did, so the open source version will list them up front.

Claude Code wrote the code. I decided what it could show, store and count.

If you have worked on abuse in community apps, I would like your help. [Get in touch](/contact).`,
};

export default article;
